import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365 Plus',
  initialServiceVisit: 'Initial Service Visit',
  oneTime: 'One-Time',
  mosquitoTickControl: 'Mosquito & Tick Control',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  statsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  currentRSI: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1f2937',
    lineHeight: 1,
  },
  signalBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: '9999px',
    letterSpacing: '0.025em',
    textTransform: 'uppercase',
  },
  metaText: {
    fontSize: '12px',
    color: '#6b7280',
  },
  svgContainer: {
    width: '100%',
    lineHeight: 0,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginTop: '12px',
    justifyContent: 'center',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  empty: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '14px',
  },
};

function getZoneLabel(value) {
  if (value >= 70) return 'Overbought';
  if (value <= 30) return 'Oversold';
  return 'Neutral';
}

function getZoneColor(value) {
  if (value >= 70) return '#dc2626';
  if (value <= 30) return '#16a34a';
  return '#2563eb';
}

function getSignalBadgeStyle(signal) {
  if (signal === 'overbought') {
    return { backgroundColor: '#fee2e2', color: '#dc2626' };
  }
  if (signal === 'oversold') {
    return { backgroundColor: '#dcfce7', color: '#16a34a' };
  }
  return { backgroundColor: '#eef2ff', color: '#4338ca' };
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function RelativeStrengthIndex({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!svgRef.current || !data || !data.dates || !data.rsi || data.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 350;
    const margin = { top: 24, right: 40, bottom: 44, left: 48 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const dates = data.dates;
    const rsi = data.rsi;
    const n = dates.length;

    const parsedDates = dates.map(d => new Date(d));

    const xScale = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerWidth]);

    const yScale = d3.scaleLinear()
      .domain([0, 100])
      .range([innerHeight, 0]);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Overbought zone (above 70) - red shading
    g.append('rect')
      .attr('x', 0)
      .attr('y', yScale(100))
      .attr('width', innerWidth)
      .attr('height', yScale(70) - yScale(100))
      .attr('fill', '#fecaca')
      .attr('fill-opacity', 0.25);

    // Oversold zone (below 30) - green shading
    g.append('rect')
      .attr('x', 0)
      .attr('y', yScale(30))
      .attr('width', innerWidth)
      .attr('height', yScale(0) - yScale(30))
      .attr('fill', '#bbf7d0')
      .attr('fill-opacity', 0.25);

    // Horizontal grid lines
    const yTicks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Dashed horizontal lines at 30, 50, 70
    [30, 50, 70].forEach(level => {
      const color = level === 70 ? '#dc2626' : level === 30 ? '#16a34a' : '#9ca3af';
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerWidth)
        .attr('y1', yScale(level))
        .attr('y2', yScale(level))
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '6,4');

      // Labels on right side
      g.append('text')
        .attr('x', innerWidth + 4)
        .attr('y', yScale(level))
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .attr('fill', color)
        .attr('font-weight', 500)
        .text(level);
    });

    // Build RSI line segments colored by zone
    // We draw segments between each pair of consecutive points, colored by the zone of the starting point
    for (let i = 0; i < n - 1; i++) {
      const val = rsi[i];
      const nextVal = rsi[i + 1];
      if (val == null || nextVal == null) continue;

      const segColor = getZoneColor(val);

      g.append('line')
        .attr('x1', xScale(parsedDates[i]))
        .attr('y1', yScale(val))
        .attr('x2', xScale(parsedDates[i + 1]))
        .attr('y2', yScale(nextVal))
        .attr('stroke', segColor)
        .attr('stroke-width', 2.5)
        .attr('stroke-linecap', 'round');
    }

    // Data points
    rsi.forEach((val, i) => {
      if (val == null) return;
      g.append('circle')
        .attr('cx', xScale(parsedDates[i]))
        .attr('cy', yScale(val))
        .attr('r', 3)
        .attr('fill', getZoneColor(val))
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1.5);
    });

    // Divergence markers
    if (data.divergences && data.divergences.length > 0) {
      data.divergences.forEach(div => {
        const divDate = new Date(div.date);
        const dateIdx = dates.findIndex(d => d === div.date);
        if (dateIdx < 0) return;
        const rsiVal = rsi[dateIdx];
        if (rsiVal == null) return;

        const cx = xScale(divDate);
        const cy = yScale(rsiVal);
        const isBearish = div.type === 'bearish';

        // Diamond marker
        const diamondSize = 7;
        const diamondPath = `M ${cx} ${cy - diamondSize} L ${cx + diamondSize} ${cy} L ${cx} ${cy + diamondSize} L ${cx - diamondSize} ${cy} Z`;
        g.append('path')
          .attr('d', diamondPath)
          .attr('fill', isBearish ? '#dc2626' : '#16a34a')
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1.5)
          .attr('opacity', 0.9);

        // Annotation line and label
        const labelY = cy - 18;
        g.append('line')
          .attr('x1', cx)
          .attr('y1', cy - diamondSize)
          .attr('x2', cx)
          .attr('y2', labelY + 4)
          .attr('stroke', isBearish ? '#dc2626' : '#16a34a')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '2,2');

        g.append('text')
          .attr('x', cx)
          .attr('y', labelY)
          .attr('text-anchor', 'middle')
          .attr('font-size', '9px')
          .attr('font-weight', 600)
          .attr('fill', isBearish ? '#dc2626' : '#16a34a')
          .text(isBearish ? 'Bear Div' : 'Bull Div');
      });
    }

    // X axis
    const xAxis = d3.axisBottom(xScale)
      .ticks(Math.min(n, 8))
      .tickSize(0)
      .tickFormat(d3.timeFormat('%b %d'));

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis)
      .call(sel => {
        sel.select('.domain').attr('stroke', '#e5e7eb');
        sel.selectAll('.tick line').attr('stroke', '#e5e7eb');
        sel.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', '10px');
      });

    // Y axis
    const yAxis = d3.axisLeft(yScale)
      .tickValues([0, 20, 30, 50, 70, 80, 100])
      .tickSize(0);

    g.append('g')
      .call(yAxis)
      .call(sel => {
        sel.select('.domain').attr('stroke', '#e5e7eb');
        sel.selectAll('.tick line').attr('stroke', '#e5e7eb');
        sel.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', '10px');
      });

    // Crosshair line (initially hidden)
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Hover dot
    const hoverDot = g.append('circle')
      .attr('r', 5)
      .attr('fill', '#2563eb')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('display', 'none');

    // Invisible overlay for hover
    const bisect = d3.bisector(d => d).left;

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .attr('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const x0 = xScale.invert(mx);
        const timestamps = parsedDates.map(d => d.getTime());
        let idx = bisect(timestamps, x0.getTime());
        if (idx > 0 && idx < n) {
          const d0 = timestamps[idx - 1];
          const d1 = timestamps[idx];
          idx = (x0.getTime() - d0) > (d1 - x0.getTime()) ? idx : idx - 1;
        }
        idx = Math.max(0, Math.min(n - 1, idx));

        const rsiVal = rsi[idx];
        if (rsiVal == null) return;

        const cx = xScale(parsedDates[idx]);
        crosshair
          .attr('x1', cx)
          .attr('x2', cx)
          .style('display', null);

        hoverDot
          .attr('cx', cx)
          .attr('cy', yScale(rsiVal))
          .attr('fill', getZoneColor(rsiVal))
          .style('display', null);

        // Check if this date has a divergence
        const divAtDate = data.divergences
          ? data.divergences.find(d => d.date === dates[idx])
          : null;

        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 10,
          date: dates[idx],
          rsi: rsiVal,
          zone: getZoneLabel(rsiVal),
          divergence: divAtDate,
        });
      })
      .on('mouseleave', () => {
        crosshair.style('display', 'none');
        hoverDot.style('display', 'none');
        setTooltip(null);
      });

  }, [data]);

  if (!data || !data.dates || !data.rsi || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <h3 style={styles.title}>Relative Strength Index (RSI)</h3>
          <p style={styles.subtitle}>Momentum oscillator for price overbought/oversold conditions</p>
        </div>
        <div style={styles.empty}>No data available</div>
      </div>
    );
  }

  const badgeStyle = getSignalBadgeStyle(data.signal);
  const signalLabel = data.signal === 'overbought'
    ? 'Overbought'
    : data.signal === 'oversold'
      ? 'Oversold'
      : 'Neutral';

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <h3 style={styles.title}>Relative Strength Index (RSI)</h3>
        <p style={styles.subtitle}>Momentum oscillator for price overbought/oversold conditions</p>
      </div>

      <div style={styles.statsRow}>
        <span style={{ ...styles.currentRSI, color: getZoneColor(data.currentRSI) }}>
          {data.currentRSI != null ? data.currentRSI.toFixed(1) : '--'}
        </span>
        <span style={{ ...styles.signalBadge, ...badgeStyle }}>
          {signalLabel}
        </span>
        <span style={styles.metaText}>
          {FIELD_LABELS[data.field] || data.field} &middot; {data.periods}-period RSI
        </span>
      </div>

      <div style={styles.svgContainer}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 350"
          preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height: 'auto' }}
        />
      </div>

      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 3, background: '#2563eb', borderRadius: 1 }} />
          <span>RSI (Neutral)</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 3, background: '#dc2626', borderRadius: 1 }} />
          <span>RSI (Overbought)</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 3, background: '#16a34a', borderRadius: 1 }} />
          <span>RSI (Oversold)</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 8, background: '#fecaca', borderRadius: 1, opacity: 0.5 }} />
          <span>Overbought Zone</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 8, background: '#bbf7d0', borderRadius: 1, opacity: 0.5 }} />
          <span>Oversold Zone</span>
        </div>
        {data.divergences && data.divergences.length > 0 && (
          <div style={styles.legendItem}>
            <svg width="12" height="12" viewBox="0 0 12 12">
              <path d="M 6 0 L 12 6 L 6 12 L 0 6 Z" fill="#6b7280" />
            </svg>
            <span>Divergence</span>
          </div>
        )}
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>RSI</span>
            <span style={{ fontWeight: 600, color: getZoneColor(tooltip.rsi) }}>
              {tooltip.rsi.toFixed(1)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Zone</span>
            <span style={{ fontWeight: 600, color: getZoneColor(tooltip.rsi) }}>
              {tooltip.zone}
            </span>
          </div>
          {tooltip.divergence && (
            <div
              style={{
                marginTop: '4px',
                paddingTop: '4px',
                borderTop: '1px solid #e5e7eb',
                fontSize: '11px',
                color: tooltip.divergence.type === 'bearish' ? '#dc2626' : '#16a34a',
                fontWeight: 600,
              }}
            >
              {tooltip.divergence.type === 'bearish' ? 'Bearish' : 'Bullish'} Divergence
              {tooltip.divergence.description && (
                <div style={{ fontWeight: 400, color: '#6b7280', marginTop: '2px' }}>
                  {tooltip.divergence.description}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
